import { useState, useEffect, useCallback } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Shield, 
  Trash2,
  Edit2,
  Copy,
  Check,
  X,
  Clock,
  Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { ClientAccessModal, PORTAL_MODULES } from '../components/ClientAccessModal';

const PERMISSION_ICONS = Object.fromEntries(PORTAL_MODULES.map(m => [m.id, m.icon]));
const PERMISSION_LABELS = Object.fromEntries(PORTAL_MODULES.map(m => [m.id, m.label]));

const ClientPortalPage = () => {
  const { get, post, put, del } = useAdminApi();
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [clientsRes, invitesRes, driversRes] = await Promise.all([
      get('/saas/portal/clients'),
      get('/saas/portal/invites'),
      get('/saas/portal/available-drivers')
    ]);
    
    if (clientsRes.success) setClients(clientsRes.data);
    if (invitesRes.success) setInvites(invitesRes.data);
    if (driversRes.success) setAvailableDrivers(driversRes.data);
    
    setLoading(false);
  }, [get]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
            <Users className="text-amber-500" size={24} />
          </div>
          Portail pour vos Clients
        </h1>
        <p className="text-slate-400 mt-2">
          Gérez l'accès de vos clients à la localisation et aux données de leurs chauffeurs assignés
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 mb-6">
        {[
          { id: 'clients', label: 'Clients', count: clients.length },
          { id: 'invites', label: 'Invitations', count: invites.filter(i => i.status === 'pending').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-amber-500 text-amber-500'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-amber-500/20' : 'bg-slate-800'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'clients' && (
        <ClientsTab 
          clients={clients} 
          onRefresh={fetchData}
          onInvite={() => setShowInviteModal(true)}
          onEdit={setEditingClient}
          api={{ put, post }}
        />
      )}
      
      {activeTab === 'invites' && (
        <InvitesTab 
          invites={invites}
          onRefresh={fetchData}
          onNewInvite={() => setShowInviteModal(true)}
          api={{ del }}
        />
      )}

      {/* Modals */}
      {showInviteModal && (
        <ClientAccessModal
          drivers={availableDrivers}
          onClose={() => setShowInviteModal(false)}
          onSave={async (data) => {
            const result = await post('/saas/portal/invites', data);
            if (result.success) {
              toast.success('Invitation envoyée');
              setShowInviteModal(false);
              fetchData();
              // Show link to copy
              if (result.data?.invite_url) {
                navigator.clipboard?.writeText(result.data.invite_url)
                  .then(() => toast.info('Lien copié dans le presse-papier'))
                  .catch(() => toast.info("Copiez le lien depuis l'onglet Invitations"));
              }
            }
          }}
        />
      )}

      {editingClient && (
        <ClientAccessModal
          client={editingClient}
          drivers={availableDrivers}
          onClose={() => setEditingClient(null)}
          onSave={async (data) => {
            const r = await put(`/saas/portal/clients/${editingClient.id}`, data);
            if (r.success) toast.success('Accès mis à jour');
            setEditingClient(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// ==================== TABS ====================

const ClientsTab = ({ clients, onRefresh, onInvite, onEdit, api }) => {
  const handleToggleAccess = async (client) => {
    const endpoint = client.is_active 
      ? `/saas/portal/clients/${client.id}/disable`
      : `/saas/portal/clients/${client.id}/enable`;
    
    const result = await api.post(endpoint, {});
    if (result.success) {
      toast.success(client.is_active ? 'Accès désactivé' : 'Accès réactivé');
      onRefresh();
    }
  };

  if (clients.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto text-slate-600 mb-4" size={48} />
        <h3 className="text-lg font-medium text-white mb-2">Aucun client</h3>
        <p className="text-slate-400 mb-4">Invitez votre premier client à accéder au portail</p>
        <button
          onClick={onInvite}
          data-testid="invite-client-btn"
          className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2"
        >
          <UserPlus size={18} />
          Inviter un client
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-400">{clients.length} client(s) enregistré(s)</p>
        <button
          onClick={onInvite}
          data-testid="invite-client-btn"
          className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2"
        >
          <UserPlus size={18} />
          Inviter un client
        </button>
      </div>

      <div className="space-y-3">
        {clients.map(client => (
          <div 
            key={client.id}
            className={`bg-slate-900 border rounded-xl p-4 ${
              client.is_active ? 'border-slate-800' : 'border-red-900/50 bg-red-950/20'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                  client.is_active ? 'bg-amber-500/20 text-amber-500' : 'bg-slate-800 text-slate-500'
                }`}>
                  {client.client_name?.[0]?.toUpperCase() || 'C'}
                </div>
                <div>
                  <h3 className="text-white font-medium">{client.client_name}</h3>
                  <p className="text-slate-400 text-sm">{client.email}</p>
                  {client.contact_name && (
                    <p className="text-slate-500 text-xs">Contact: {client.contact_name}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(client)}
                  data-testid={`client-edit-${client.id}`}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => handleToggleAccess(client)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    client.is_active 
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  }`}
                >
                  {client.is_active ? 'Désactiver' : 'Réactiver'}
                </button>
              </div>
            </div>

            {/* Groups & Permissions */}
            <div className="mt-4 flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Chauffeurs visibles</p>
                <span className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300" data-testid={`client-drivers-count-${client.id}`}>
                  {client.driver_ids?.length || 0} chauffeur{(client.driver_ids?.length || 0) > 1 ? 's' : ''}
                </span>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Modules visibles</p>
                <div className="flex flex-wrap gap-1">
                  {client.permissions?.filter(p => PERMISSION_LABELS[p]).map(p => {
                    const Icon = PERMISSION_ICONS[p] || Shield;
                    return (
                      <span key={p} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300 flex items-center gap-1">
                        <Icon size={12} />
                        {PERMISSION_LABELS[p]}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Last login */}
            {client.last_login && (
              <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                <Clock size={12} />
                Dernière connexion: {new Date(client.last_login).toLocaleString('fr-CA')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const InvitesTab = ({ invites, onRefresh, onNewInvite, api }) => {
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyLink = (invite) => {
    const url = `${window.location.origin}/client-portal/accept-invite/${invite.invite_token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(invite.id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Lien copié');
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm('Révoquer cette invitation ?')) return;
    const result = await api.del(`/saas/portal/invites/${invite.id}`);
    if (result.success) {
      toast.success('Invitation révoquée');
      onRefresh();
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-400',
      accepted: 'bg-green-500/20 text-green-400',
      expired: 'bg-slate-500/20 text-slate-400',
      revoked: 'bg-red-500/20 text-red-400'
    };
    const labels = {
      pending: 'En attente',
      accepted: 'Acceptée',
      expired: 'Expirée',
      revoked: 'Révoquée'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-slate-400">{invites.length} invitation(s)</p>
        <button
          onClick={onNewInvite}
          className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2"
        >
          <Mail size={18} />
          Nouvelle invitation
        </button>
      </div>

      {invites.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-medium text-white mb-2">Aucune invitation</h3>
          <p className="text-slate-400">Les invitations envoyées apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invites.map(invite => (
            <div 
              key={invite.id}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-medium">{invite.client_name}</h3>
                    {getStatusBadge(invite.status)}
                  </div>
                  <p className="text-slate-400 text-sm">{invite.email}</p>
                </div>
                
                {invite.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopyLink(invite)}
                      className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg flex items-center gap-1"
                    >
                      {copiedId === invite.id ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                    <button
                      onClick={() => handleRevoke(invite)}
                      className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg"
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Créée le {new Date(invite.created_at).toLocaleDateString('fr-CA')}</span>
                {invite.status === 'pending' && (
                  <span>Expire le {new Date(invite.expires_at).toLocaleDateString('fr-CA')}</span>
                )}
                {invite.accepted_at && (
                  <span>Acceptée le {new Date(invite.accepted_at).toLocaleDateString('fr-CA')}</span>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-1">
                {invite.groups?.map(g => (
                  <span 
                    key={g.name}
                    className="px-2 py-0.5 rounded text-xs"
                    style={{ backgroundColor: `${g.color}20`, color: g.color }}
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MODALS ====================

export default ClientPortalPage;
